import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { type MouseEvent, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'
import {
  type Consignment,
  useCancelConsignmentMutation,
  useGetConsignmentsQuery,
} from '../../store/api/consignmentApi'
import ConsignmentFormDialog from './ConsignmentFormDialog'
import { usePagePermission } from '../../hooks/usePagePermission'

type FilterKind = 'search' | 'clientCode' | 'certification'
type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = { key: keyof Consignment; label: string; kind?: ColumnKind; align?: 'right' }

const ALL_ROWS_PAGE_SIZE = 1_000_000
const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Column order + captions match DocConvert_Consignment §8.5.2. */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'contractNo', label: 'Contract No' },
  { key: 'contractDate', label: 'Contract Date', kind: 'date' },
  { key: 'clientCode', label: 'Client Ref' },
  { key: 'clientName', label: 'Client' },
  { key: 'quantityKg', label: 'Quantity (Kg)', kind: 'number', align: 'right' },
  { key: 'packaging', label: 'Package' },
  { key: 'qualityCode', label: 'Quality' },
  { key: 'certification', label: 'Certification' },
  { key: 'lastFixingDate', label: 'Last Fixing Date', kind: 'date' },
  { key: 'dateUpdate', label: 'Updated Date', kind: 'datetime' },
  { key: 'personUpdate', label: 'Updated Person' },
]

const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  textTransform: 'none',
  fontSize: '0.7rem',
  lineHeight: 1.1,
  '&.MuiButton-outlined': {
    borderColor: alpha(theme.palette.text.primary, 0.14),
    '&:hover': {
      borderColor: alpha(theme.palette.text.primary, 0.24),
      backgroundColor: alpha(theme.palette.text.primary, 0.04),
    },
  },
})

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const distinctValues = (rows: Consignment[], key: keyof Consignment): string[] => {
  const values = new Set<string>()
  for (const row of rows) {
    const value = row[key]
    if (value == null) continue
    const text = String(value).trim()
    if (text) values.add(text)
  }
  return [...values].sort((a, b) => a.localeCompare(b))
}

const rowMatchesSearch = (row: Consignment, term: string): boolean => {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  return TABLE_COLUMNS.some((col) => {
    const value = row[col.key]
    return value != null && String(value).toLowerCase().includes(needle)
  })
}

const sumNumericColumn = (rows: Consignment[], key: keyof Consignment): number | null => {
  let total = 0
  let hasValue = false
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || Number.isNaN(value)) continue
    total += value
    hasValue = true
  }
  return hasValue ? total : null
}

export default function ConsignmentListView() {
  const { canEdit } = usePagePermission()
  const [selectedContractNo, setSelectedContractNo] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [clientCodeFilter, setClientCodeFilter] = useState('')
  const [certificationFilter, setCertificationFilter] = useState('')
  const [filterPopover, setFilterPopover] = useState<{ kind: FilterKind; anchor: HTMLElement } | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'new' | 'edit'>('new')
  const [cancelConsignment, { isLoading: isDeleting }] = useCancelConsignmentMutation()

  const { data, isLoading, isFetching, error } = useGetConsignmentsQuery({
    page: 1,
    pageSize: ALL_ROWS_PAGE_SIZE,
  })

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(
    () =>
      baseRows.filter((row) => {
        if (!rowMatchesSearch(row, searchTerm)) return false
        if (clientCodeFilter.trim() && row.clientCode !== clientCodeFilter.trim()) return false
        if (certificationFilter && row.certification !== certificationFilter) return false
        return true
      }),
    [baseRows, certificationFilter, clientCodeFilter, searchTerm],
  )
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return filteredRows
    const start = page * rowsPerPage
    return filteredRows.slice(start, start + rowsPerPage)
  }, [filteredRows, page, rowsPerPage])
  const selectedRow = useMemo(
    () => baseRows.find((row) => row.contractNo === selectedContractNo) ?? null,
    [baseRows, selectedContractNo],
  )
  const totalRowValues = useMemo(
    () =>
      new Map(
        TABLE_COLUMNS.filter((col) => col.kind === 'number').map((col) => [
          col.key,
          sumNumericColumn(filteredRows, col.key),
        ]),
      ),
    [filteredRows],
  )
  const clientCodeOptions = useMemo(() => distinctValues(baseRows, 'clientCode'), [baseRows])
  const certificationOptions = useMemo(() => distinctValues(baseRows, 'certification'), [baseRows])
  const hasActiveFilters = Boolean(searchTerm.trim() || clientCodeFilter.trim() || certificationFilter)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`

  const [selectOpen, setSelectOpen] = useState(false)
  const closeFilterPopover = () => { setFilterPopover(null); setSelectOpen(false) }
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => {
    setFilterPopover({ kind, anchor: e.currentTarget })
    if (kind !== 'search') setSelectOpen(true)
  }

  const clearAllFilters = () => {
    setSearchTerm('')
    setClientCodeFilter('')
    setCertificationFilter('')
    setPage(0)
    closeFilterPopover()
  }

  const openDialog = (mode: 'new' | 'edit') => {
    if (mode === 'edit' && !selectedRow) {
      window.alert('Please choose a record!')
      return
    }
    setDialogMode(mode)
    setDialogOpen(true)
  }

  const handleDelete = async () => {
    if (!selectedRow) {
      window.alert('Please choose a record!')
      return
    }
    const ok = window.confirm(`Do you want to delete this consignment = '${selectedRow.contractNo}'?`)
    if (!ok) return
    try {
      await cancelConsignment(selectedRow.contractNo).unwrap()
      setSelectedContractNo(null)
      window.alert('Successfully Deleted')
    } catch {
      window.alert('Could not delete consignment.')
    }
  }

  const cellValue = (row: Consignment, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'datetime') return formatAccessDateTime(val)
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'number') return formatAccessNumber(val)
    return String(val)
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
            Filter
          </Typography>
          <Button
            size="small"
            variant={searchTerm.trim() ? 'contained' : 'outlined'}
            color={searchTerm.trim() ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('search', e)}
            sx={filterBarBtnSx}
          >
            Search
          </Button>
          <Button
            size="small"
            variant={clientCodeFilter.trim() ? 'contained' : 'outlined'}
            color={clientCodeFilter.trim() ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('clientCode', e)}
            sx={filterBarBtnSx}
          >
            Client
          </Button>
          <Button
            size="small"
            variant={certificationFilter ? 'contained' : 'outlined'}
            color={certificationFilter ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('certification', e)}
            sx={filterBarBtnSx}
          >
            Certification
          </Button>
          {hasActiveFilters && (
            <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>
              Clear All
            </Button>
          )}
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => openDialog('new')}>
            New
          </Button>
          <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={() => openDialog('edit')}>
            Edit
          </Button>
          <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={!selectedRow || isDeleting || !canEdit} onClick={handleDelete}>
            Delete
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load consignments.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, minHeight: 30, py: 0.125 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching || isDeleting) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.68rem' }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.68rem' }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{
                '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.72rem' },
                '& .MuiTablePagination-select': { fontSize: '0.72rem' },
                '& .MuiTablePagination-actions': { transform: 'scale(0.92)', transformOrigin: 'center' },
              }}
            />
          </Stack>
        </Box>

        <Popover
          open={filterPopover !== null}
          anchorEl={filterPopover?.anchor ?? null}
          onClose={closeFilterPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { p: 1.5, minWidth: 240, maxWidth: 320 } }}
        >
          {filterPopover?.kind === 'search' && (
            <Stack spacing={1}>
              <TextField
                size="small"
                fullWidth
                autoFocus
                label="Search consignments"
                placeholder="Contract no, client..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setPage(0)
                }}
              />
              <Button size="small" variant="text" onClick={() => { setSearchTerm(''); setPage(0); closeFilterPopover() }}>
                Clear Search
              </Button>
            </Stack>
          )}
          {filterPopover?.kind === 'clientCode' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Client</InputLabel>
              <Select
                label="Client"
                value={clientCodeFilter}
                open={selectOpen}
                onOpen={() => setSelectOpen(true)}
                onClose={() => { setSelectOpen(false); closeFilterPopover() }}
                onChange={(e) => { setClientCodeFilter(String(e.target.value)); setPage(0) }}
              >
                <MenuItem value="">All / Clear</MenuItem>
                {clientCodeOptions.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {filterPopover?.kind === 'certification' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Certification</InputLabel>
              <Select
                label="Certification"
                value={certificationFilter}
                open={selectOpen}
                onOpen={() => setSelectOpen(true)}
                onClose={() => { setSelectOpen(false); closeFilterPopover() }}
                onChange={(e) => { setCertificationFilter(String(e.target.value)); setPage(0) }}
              >
                <MenuItem value="">All / Clear</MenuItem>
                {certificationOptions.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </Select>
            </FormControl>
          )}
        </Popover>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell key={col.key} align={col.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            )}
            fixedFooterContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col, index) => {
                  const total = totalRowValues.get(col.key)
                  const val = total == null ? '' : formatAccessNumber(total)
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      sx={{
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        py: 0.5,
                        bgcolor: 'background.paper',
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {index === 0 ? 'Total' : val}
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_: number, row: Consignment) =>
              TABLE_COLUMNS.map((col) => {
                const val = cellValue(row, col)
                return (
                  <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                    <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>
                      {val || '-'}
                    </Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 1200, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const row = Number.isFinite(idx) ? rows[idx] : null
                return (
                  <TableRow
                    {...props}
                    hover
                    selected={row != null && row.contractNo === selectedContractNo}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (row) setSelectedContractNo(row.contractNo)
                    }}
                  />
                )
              },
            }}
          />
        </Box>
      </Box>

      <ConsignmentFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        mode={dialogMode}
        consignment={dialogMode === 'edit' ? selectedRow : null}
      />
    </Box>
  )
}
